import { useNavigate, useParams, useSearch } from "@tanstack/react-router";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCan } from "@/lib/auth";

import { currentMonth, monthLabel, recentMonths, useCosLineItems } from "../api";
import { CalculationsView } from "../components/CalculationsView";
import { DetailsView } from "../components/DetailsView";
import { FlowMapView } from "../components/FlowMapView";
import { HowToView } from "../components/HowToView";
import { MappingsView } from "../components/MappingsView";
import { SummaryView } from "../components/SummaryView";
import { UploadsView } from "../components/UploadsView";
import { VendorsView } from "../components/VendorsView";

export type CosTab =
  | "summary"
  | "details"
  | "uploads"
  | "mappings"
  | "vendors"
  | "visual-map"
  | "calculations"
  | "how-to";

/**
 * The COS-Units workbench. Six tabs; read access sees Summary + Details only,
 * write access gets the operational tabs (Uploads / Mappings / Calculations /
 * How to use) on top. The month lives in the URL path (shared across tabs),
 * the tab in the search string — so both survive refresh and are linkable.
 */
export function CosUnitsPage() {
  const canWrite = useCan("cos_units.write");
  const canMap = useCan("cos_units.map") || canWrite;
  const navigate = useNavigate();
  const { period: routePeriod } = useParams({ strict: false }) as { period?: string };
  const period = routePeriod ?? currentMonth();
  const search = useSearch({ strict: false }) as { tab?: CosTab };
  const requested: CosTab = search.tab ?? "summary";
  const writeTabs: CosTab[] = [
    "uploads",
    "mappings",
    "vendors",
    "visual-map",
    "calculations",
    "how-to",
  ];
  const tab: CosTab = !canWrite && writeTabs.includes(requested) ? "summary" : requested;

  // The unmapped count badges the Mappings tab — it's the month's to-do list.
  const unmappedProbe = useCosLineItems(
    period,
    { limit: 1, offset: 0, status: "unmapped" },
    canMap,
  );
  const unmapped = unmappedProbe.data?.unmapped_rows ?? 0;

  const go = (next: Partial<{ period: string; tab: CosTab }>) =>
    navigate({
      to: "/cos-units/$period",
      params: { period: next.period ?? period },
      search: { tab: next.tab ?? tab },
      replace: true,
    });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">COS — Units</h1>
          <p className="max-w-3xl text-sm text-muted-foreground">
            The monthly COGS-per-unit workbench. Upload the month&apos;s cost files, map
            every invoice line to what we sell, and track the $/metric that feeds
            margin reporting — the spreadsheet&apos;s Simple Summary, minus the spreadsheet.
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Cost month
          </span>
          <Select value={period} onValueChange={(p) => go({ period: p })}>
            <SelectTrigger className="h-9 w-[150px]">
              <SelectValue>{monthLabel(period)}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {(recentMonths(18).includes(period)
                ? recentMonths(18)
                : [period, ...recentMonths(18)]
              ).map((m) => (
                <SelectItem key={m} value={m}>
                  {monthLabel(m)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(t) => go({ tab: t as CosTab })}>
        <TabsList variant="underline">
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
          {canWrite && <TabsTrigger value="uploads">Uploads</TabsTrigger>}
          {canWrite && (
            <TabsTrigger value="mappings" count={unmapped > 0 ? unmapped : undefined}>
              Mappings
            </TabsTrigger>
          )}
          {canWrite && <TabsTrigger value="vendors">Vendors</TabsTrigger>}
          {canWrite && <TabsTrigger value="visual-map">Visual map</TabsTrigger>}
          {canWrite && <TabsTrigger value="calculations">Calculations</TabsTrigger>}
          {canWrite && <TabsTrigger value="how-to">How to use</TabsTrigger>}
        </TabsList>

        <TabsContent value="summary" className="mt-4">
          <SummaryView onOpenMonth={(m) => go({ period: m, tab: "details" })} />
        </TabsContent>
        <TabsContent value="details" className="mt-4">
          <DetailsView period={period} canWrite={canWrite} />
        </TabsContent>
        {canWrite && (
          <TabsContent value="uploads" className="mt-4">
            <UploadsView period={period} />
          </TabsContent>
        )}
        {canWrite && (
          <TabsContent value="mappings" className="mt-4">
            <MappingsView period={period} canMap={canMap} />
          </TabsContent>
        )}
        {canWrite && (
          <TabsContent value="vendors" className="mt-4">
            <VendorsView period={period} canMap={canMap} />
          </TabsContent>
        )}
        {canWrite && (
          <TabsContent value="visual-map" className="mt-4">
            <FlowMapView period={period} />
          </TabsContent>
        )}
        {canWrite && (
          <TabsContent value="calculations" className="mt-4">
            <CalculationsView period={period} />
          </TabsContent>
        )}
        {canWrite && (
          <TabsContent value="how-to" className="mt-4">
            <HowToView />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
